import { create } from 'zustand';
import { offlineDB } from './offlineDB';
import { useCuttingStore } from './useCuttingStore';
import { useSewingStore } from './useSewingStore';
import { useInlineQCStore } from './useInlineQCStore';
import { useFinalQCStore } from './useFinalQCStore';
import { useRoughBodyStore } from './useRoughBodyStore';

interface OfflineState {
    queueSize: number;
    isOnline: boolean;
    isSyncing: boolean;

    // Actions
    addToQueue: (action: string, payload: any) => Promise<void>;
    sync: () => Promise<void>;
    checkConnection: () => void;
    initialize: () => Promise<void>;
}

export const useOfflineQueue = create<OfflineState>((set, get) => ({
    queueSize: 0,
    isOnline: navigator.onLine,
    isSyncing: false,

    initialize: async () => {
        // Load initial size
        const db = await offlineDB.open();
        const count = await db.count('offline_queue');
        set({ queueSize: count });

        // Listeners
        window.addEventListener('online', () => {
            set({ isOnline: true });
            get().sync();
        });
        window.addEventListener('offline', () => set({ isOnline: false }));
    },

    checkConnection: () => set({ isOnline: navigator.onLine }),

    addToQueue: async (action, payload) => {
        await offlineDB.enqueue(action, payload);
        const db = await offlineDB.open();
        const count = await db.count('offline_queue');
        set({ queueSize: count });
    },

    sync: async () => {
        const { isOnline, isSyncing } = get();
        if (!isOnline || isSyncing) return;

        set({ isSyncing: true });

        try {
            const db = await offlineDB.open();
            const pending = await db.getAll('offline_queue');

            if (pending.length === 0) {
                set({ isSyncing: false, queueSize: 0 });
                return;
            }

            // Process sequentially
            for (const item of pending) {
                let success = false;

                try {
                    switch (item.action) {
                        case 'startCutting': {
                            const cuttingStore = useCuttingStore.getState();
                            await cuttingStore.startCutting(item.payload.sampleId, item.payload.startDate);
                            success = true;
                            break;
                        }
                        case 'endCutting': {
                            const cuttingStore = useCuttingStore.getState();
                            await cuttingStore.endCutting(item.payload.sampleId, item.payload.endDate, item.payload.remarks, item.payload.leftoverFabric);
                            success = true;
                            break;
                        }
                        case 'assignPieceToTailor': {
                            const sewingStore = useSewingStore.getState();
                            await sewingStore.assignPieceToTailor(item.payload.sampleId, item.payload.pieceId, item.payload.tailorId, item.payload.tailorName, item.payload.startDate);
                            success = true;
                            break;
                        }
                        case 'completePieceSewing': {
                            const sewingStore = useSewingStore.getState();
                            await sewingStore.completePieceSewing(item.payload.sampleId, item.payload.pieceId, item.payload.endDate);
                            success = true;
                            break;
                        }
                        case 'internalQCPiece': {
                            const inlineQCStore = useInlineQCStore.getState();
                            await inlineQCStore.internalQCPiece(item.payload.sampleId, item.payload.pieceId, item.payload.passed, item.payload.defects, item.payload.notes, item.payload.date);
                            success = true;
                            break;
                        }
                        case 'qcInspectorPiece': {
                            const finalQCStore = useFinalQCStore.getState();
                            await finalQCStore.qcInspectorPiece(item.payload.sampleId, item.payload.pieceId, item.payload.passed, item.payload.defects, item.payload.notes, item.payload.date);
                            success = true;
                            break;
                        }
                        // Rough Body Actions
                        case 'roughBodyUpdateStatus':
                            const roughStore = useRoughBodyStore.getState();
                            await roughStore.updateStationStatus(item.payload.id, item.payload.stage, item.payload.action, item.payload.payload);
                            success = true;
                            break;
                        case 'roughBodyMarkDelivered':
                            const roughStore2 = useRoughBodyStore.getState();
                            await roughStore2.markAsDelivered(item.payload.id, item.payload.notes);
                            success = true;
                            break;
                        default:
                            console.warn("Unknown offline action:", item.action);
                            // Delete anyway to avoid block? Or keep?
                            // If unknown, we should probably delete it to unblock queue.
                            success = true;
                            break;
                    }

                    if (success) {
                        await db.delete('offline_queue', item.id);
                    }

                } catch (err) {
                    console.error("Sync failed for item", item, err);
                    // If error is NOT network related (e.g. logic error), we might want to move it to 'failed' list?
                    // For now, retry later. Loop continues? 
                    // We should break to avoid out-of-order execution if dependencies exist.
                    break;
                }
            }

            // Update count
            const count = await db.count('offline_queue');
            set({ queueSize: count });

        } catch (e) {
            console.error("Sync process error", e);
        } finally {
            set({ isSyncing: false });
        }
    }
}));
